import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { talentAPI, clockAPI } from '../../utils/api';
import { acquireLocation } from '../../utils/geolocation';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, MapPin, Calendar, Clock, DollarSign, LogIn, LogOut, Loader2, CheckCircle, XCircle, AlertTriangle, MessageCircle } from 'lucide-react';
import { isFlatPay, formatPay } from '../../utils/payFormat';
import { formatTime12h, formatTimeRange, timezoneLabel, parseLocalDate, formatDate } from '../../utils/timeFormat';
import { toast } from 'sonner';
import TalentExpensesPanel from '../../components/TalentExpensesPanel';
import ShiftLeaderRosterPanel from '../../components/ShiftLeaderRosterPanel';
import StatusChip from '../../components/StatusChip';
import BookingStatusBadge from '../../components/BookingStatusBadge';
import AssignmentBadge from '../../components/AssignmentBadge';
import BackupRoleNoticeModal from '../../components/BackupRoleNoticeModal';
import AddToCalendarButton from '../../components/AddToCalendarButton';
import GigCodeOfConductModal from '../../components/GigCodeOfConductModal';

// 2026-08-06 P0 (Samantha LaCroix bug) — statuses that count as "still
// upcoming" for the talent Booked Jobs / Upcoming list. Previously we
// only accepted 'confirmed' + 'in_progress' — anything mid-flow (a
// backend transition to 'day_confirmed', an old 'pending' row, etc.)
// would silently vanish from the talent's Booked Jobs page while
// reminders continued to arrive. Widen this to cover every pre-shift
// status that should still be visible.
const ACTIVE_BOOKING_STATUSES = new Set([
  'confirmed', 'in_progress', 'day_confirmed',
  'day_confirm_requested', 'pending', 'booked', 'checked_in',
]);

const TalentBookings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockModal, setClockModal] = useState(null);
  const [clockStatus, setClockStatus] = useState({});
  const [processing, setProcessing] = useState(false);
  const [releaseModal, setReleaseModal] = useState(null);
  const [releasing, setReleasing] = useState(false);
  // Within-24h cancellation → self-release is locked; direct to Evie.
  const [contactEvieModal, setContactEvieModal] = useState(null);
  // Iter-133x - active Backup Role ack modal target
  const [backupAckBookingId, setBackupAckBookingId] = useState(null);
  // 2026-08-06 P0 — highlight ID for `?focus=<id>` or `?action=day-confirm`
  // deep links coming from reminder emails / SMS.
  const [highlightBookingId, setHighlightBookingId] = useState(null);
  // 2026-09 — Code of Conduct gig-acceptance gate.
  const [cocBookingId, setCocBookingId] = useState(null);
  const [cocBusy, setCocBusy] = useState(false);

  const acceptGig = async (versionId) => {
    if (!cocBookingId) return;
    setCocBusy(true);
    try {
      await api.post(`/talent/bookings/${cocBookingId}/accept-gig`, {
        coc_version_id: versionId,
        coc_agreed: true,
      });
      toast.success('Gig accepted — you agreed to the Code of Conduct.');
      setCocBookingId(null);
      fetchBookings();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not accept the gig.'));
    } finally {
      setCocBusy(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // 2026-08-06 P0 — Parse deep-link params so a talent tapping a
  // day-of-confirmation reminder lands with the correct booking
  // scrolled into view and the day-confirm banner clearly visible
  // (instead of, e.g., a Clock In button that looked like an
  // auto-confirmation).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focus = params.get('focus') || params.get('booking');
    const action = params.get('action') || '';
    if (!focus && !action) return;
    if (focus) setHighlightBookingId(focus);
    // Wait for cards to render then scroll to the target booking.
    const t = setTimeout(() => {
      const el = focus ? document.querySelector(`[data-booking-id="${focus}"]`) : null;
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (action === 'day-confirm' || action === 'confirm') {
        toast.info('Please tap "Confirm shift" below to confirm your assignment.');
      }
    }, 700);
    return () => clearTimeout(t);
  }, [location.search]);

  const fetchBookings = async () => {
    try {
      const response = await talentAPI.getBookings();
      setBookings(response.data);
      
      // Fetch clock status for today's bookings. 2026-08-06 —
      // widened to any active booking status (not just 'confirmed')
      // so we still know whether a talent has clocked in when the
      // status is 'day_confirmed' or 'in_progress'.
      const todayBookings = response.data.filter(
        (b) => isToday(b.shift_date, b.timezone) && ACTIVE_BOOKING_STATUSES.has(b.status)
      );
      for (const booking of todayBookings) {
        try {
          const statusRes = await clockAPI.getStatus(booking.id);
          setClockStatus(prev => ({ ...prev, [booking.id]: statusRes.data }));
        } catch (e) {
          // Ignore individual status fetch failures
        }
      }
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseBooking = async () => {
    if (!releaseModal) return;
    
    setReleasing(true);
    try {
      await api.post(`/talent/bookings/${releaseModal.id}/release`);
      toast.success('Booking released successfully. The gig is now available for others.');
      setReleaseModal(null);
      fetchBookings();
    } catch (error) {
      // Iter-370 — Backend returns structured `{code, message}` details;
      // preserve legacy string fallback for older payloads.
      const detail = error.response?.data?.detail;
      // Within-24h lock — not a technical failure. Route the talent to Evie
      // instead of showing a red error toast or asking them to retry.
      if (detail && typeof detail === 'object' && detail.code === 'within_24h_contact_required') {
        setReleaseModal(null);
        setContactEvieModal(releaseModal);
        return;
      }
      const msg = (detail && typeof detail === 'object' && detail.message)
        ? detail.message
        : (typeof detail === 'string' ? detail : 'Failed to release booking');
      toast.error(msg, { duration: 8000 });
    } finally {
      setReleasing(false);
    }
  };

  const handleClockIn = async (booking) => {
    // Iter-133x - block clock-in until backup acknowledgment exists
    if (booking.assignment_type === 'backup' && !booking.backup_acknowledged) {
      setClockModal(null);
      setBackupAckBookingId(booking.id);
      toast.error('Please acknowledge the backup role terms before clocking in.');
      return;
    }
    setProcessing(true);
    try {
      let lat = null, lng = null;
      try {
        // P0 (2026-08-18, Anita Currie / Dallas) — two-stage acquisition.
        const loc = await acquireLocation();
        lat = loc.latitude;
        lng = loc.longitude;
      } catch (e) {
        // Location is optional per the clock routes
      }

      const res = await clockAPI.clockIn({ booking_id: booking.id, latitude: lat, longitude: lng });
      toast.success(res.data.message || 'Clocked in successfully!');
      setClockModal(null);
      fetchBookings();
    } catch (error) {
      // Mel directive 2026-08-18 — surface structured detail bodies so
      // `ggf_cert_required` deep-links to the assessment, and other
      // structured errors show their message + hint clearly.
      const d = error.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'ggf_cert_required') {
        toast.error(d.message || 'GGF assessment required before check-in.', {
          duration: 8000,
          action: {
            label: 'Take assessment',
            onClick: () => navigate(d.next_step || '/talent/certifications/ggf'),
          },
        });
        return;
      }
      const msg = typeof d === 'string'
        ? d
        : (d && (d.message || d.hint || d.code)) || 'Failed to clock in';
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleClockOut = async (booking) => {
    setProcessing(true);
    try {
      let lat = null, lng = null;
      try {
        const loc = await acquireLocation();
        lat = loc.latitude;
        lng = loc.longitude;
      } catch (_e) {
        // Location is optional per the clock routes — proceed without
      }

      const res = await clockAPI.clockOut({ booking_id: booking.id, latitude: lat, longitude: lng });
      toast.success(`Clocked out! Hours worked: ${res.data.hours_worked}`);
      setClockModal(null);
      fetchBookings();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to clock out';
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'bg-green-100 text-green-800',
      in_progress: 'bg-cyan-100 text-cyan-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Iter-162c — parseLocalDate prevents the YYYY-MM-DD → UTC midnight
  // off-by-one timezone drift that was rendering May 7 shifts as May 6
  // for talent in negative-UTC time zones.
  const isUpcoming = (shiftDate) => {
    const d = parseLocalDate(shiftDate);
    return d ? d >= new Date(new Date().setHours(0, 0, 0, 0)) : false;
  };
  const isPast = (shiftDate) => {
    const d = parseLocalDate(shiftDate);
    return d ? d < new Date(new Date().setHours(0, 0, 0, 0)) : false;
  };
  /**
   * Iter-133w P0 — event-timezone-aware "today" check.
   *
   * Prior version used `new Date("YYYY-MM-DD")` which JS parses as
   * UTC midnight. For a talent in Central Time that midnight lands in
   * the PREVIOUS local day, so a tomorrow-in-DB shift falsely showed
   * "Today" on the dashboard and exposed a Clock In button that the
   * backend then correctly blocked ("Clock-in window opens in 1430
   * minutes"). Compare using the event's own timezone instead.
   */
  const isToday = (shiftDate, eventTz) => {
    if (!shiftDate) return false;
    // Parse the bare YYYY-MM-DD as a LOCAL calendar date, not UTC.
    const dateStr = typeof shiftDate === 'string' ? shiftDate.slice(0, 10) : null;
    if (!dateStr) return false;
    const tz = eventTz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    // What's "today" in the event's timezone?
    const todayInTz = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date()); // "YYYY-MM-DD"
    return dateStr === todayInTz;
  };

  const getClockState = (booking) => {
    const status = clockStatus[booking.id];
    if (!status) return 'not_loaded';
    if (status.is_clocked_out) return 'clocked_out';
    if (status.is_clocked_in) return 'clocked_in';
    return 'ready';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="talent-bookings-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/talent/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <h1 className="text-3xl font-bold">My Bookings · Classic</h1>
          <Button
            variant="outline"
            onClick={() => navigate('/talent/bookings')}
            data-testid="try-new-bookings-view"
            className="border-cyan-500 text-cyan-700 hover:bg-cyan-50"
          >
            ✨ Back to the new consolidated view
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        ) : bookings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">No bookings yet</p>
              <p className="text-sm text-muted-foreground mb-4">Apply to gigs to get booked by clients</p>
              <Button onClick={() => navigate('/talent/gigs')} className="bg-cyan-500 hover:bg-cyan-600">
                Browse Available Gigs
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Upcoming Bookings */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Upcoming Gigs</h2>
              <div className="grid gap-4" data-testid="upcoming-bookings">
                {bookings.filter((b) => isUpcoming(b.shift_date) && ACTIVE_BOOKING_STATUSES.has(b.status)).length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      No upcoming gigs
                    </CardContent>
                  </Card>
                ) : (
                  bookings
                    .filter((b) => isUpcoming(b.shift_date) && ACTIVE_BOOKING_STATUSES.has(b.status))
                    .map((booking) => {
                      const clockState = getClockState(booking);
                      const status = clockStatus[booking.id];
                      const isHighlighted = highlightBookingId === booking.id;
                      return (
                        <Card
                          key={booking.id}
                          data-testid="booking-card"
                          data-booking-id={booking.id}
                          className={`hover:shadow-md transition-shadow cursor-pointer ${isHighlighted ? 'ring-2 ring-cyan-400 shadow-lg' : ''}`}
                          onClick={(e) => { if (e.target.closest('button,a,[role="button"]')) return; navigate(`/talent/bookings/${booking.id}`); }}
                        >
                          <CardContent className="p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-3 flex-wrap">
                                  <h3 className="text-lg font-semibold">{booking.role_name}</h3>
                                  {['checked_in', 'completed', 'timesheet_approved', 'in_progress', 'day_confirmed'].includes((booking.status || '').toLowerCase())
                                    ? <StatusChip status={booking.status} />
                                    : <BookingStatusBadge booking={booking} size="md" />}
                                  <AssignmentBadge type={booking.assignment_type || 'primary'} size="md" />
                                  {isToday(booking.shift_date, booking.timezone) && (
                                    <Badge className="bg-cyan-100 text-cyan-800">Today</Badge>
                                  )}
                                </div>

                                {/* Iter-133x — per-booking inline backup-ack warning */}
                                {booking.assignment_type === 'backup' && !booking.backup_acknowledged && (
                                  <div
                                    className="mb-3 flex items-center gap-2 px-3 py-2 rounded bg-amber-50 border border-amber-300 text-amber-800 text-sm"
                                    data-testid={`backup-ack-inline-${booking.id}`}
                                  >
                                    <span className="font-medium">Backup role — acknowledgment required before clock-in.</span>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="ml-auto border-amber-400 bg-amber-100 text-amber-900 hover:bg-amber-200 h-7 text-xs"
                                      onClick={() => setBackupAckBookingId(booking.id)}
                                      data-testid={`backup-ack-open-${booking.id}`}
                                    >
                                      Review &amp; agree
                                    </Button>
                                  </div>
                                )}

                                {/* 2026-09 — Gig acceptance + Code of Conduct (Booked → Accepted → Confirmed). */}
                                {booking.acceptance_pending && (
                                  <div
                                    className="mb-3 flex flex-wrap items-center gap-2 px-3 py-2.5 rounded border-l-4 border-teal-500 bg-teal-50 text-teal-900 text-sm"
                                    data-testid={`accept-gig-banner-${booking.id}`}
                                  >
                                    <span className="font-semibold">Acceptance Pending</span>
                                    <span className="text-xs text-teal-800/90 flex-1 min-w-[220px]">
                                      An admin booked you. Review and agree to the Code of Conduct to accept this gig — this is separate from your day-of shift confirmation.
                                    </span>
                                    <Button
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); setCocBookingId(booking.id); }}
                                      className="bg-teal-600 hover:bg-teal-700 text-white h-7 text-xs"
                                      data-testid={`accept-gig-btn-${booking.id}`}
                                    >
                                      Accept Gig
                                    </Button>
                                  </div>
                                )}
                                {!booking.acceptance_pending && booking.talent_accepted && booking.talent_accepted_at && (
                                  <div
                                    className="mb-3 flex items-center gap-1.5 px-2 py-1 rounded bg-teal-50 border border-teal-200 text-teal-800 text-xs"
                                    data-testid={`gig-accepted-badge-${booking.id}`}
                                  >
                                    <span className="font-medium">
                                      ✓ Gig accepted · {new Date(booking.talent_accepted_at).toLocaleString()}
                                    </span>
                                  </div>
                                )}

                                {/* Sprint-Confirmation-Integrity (Feb 2026) — Awaiting talent confirmation */}
                                {!booking.acceptance_pending && booking.awaiting_confirmation && (
                                  <div
                                    className="mb-3 flex flex-wrap items-center gap-2 px-3 py-2.5 rounded border-l-4 border-amber-500 bg-amber-50 text-amber-900 text-sm"
                                    data-testid={`awaiting-confirmation-banner-${booking.id}`}
                                  >
                                    <span className="font-semibold">
                                      Awaiting Your Confirmation
                                    </span>
                                    <span className="text-xs text-amber-800/90 flex-1 min-w-[220px]">
                                      An admin booked you for this shift. Please confirm you can work it — check-in is blocked until you accept.
                                    </span>
                                    <Button
                                      size="sm"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          await api.post('/shift-confirmation/confirm', {
                                            booking_id: booking.id,
                                            source: 'talent_portal',
                                          });
                                          toast.success('Booking confirmed');
                                          fetchBookings();
                                        } catch (err) {
                                          const detail = err?.response?.data?.detail || 'Failed to confirm';
                                          toast.error(typeof detail === 'string' ? detail : detail.message || 'Failed to confirm');
                                        }
                                      }}
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                                      data-testid={`confirm-booking-btn-${booking.id}`}
                                    >
                                      Confirm booking
                                    </Button>
                                  </div>
                                )}
                                {!booking.awaiting_confirmation && booking.confirmed_at && booking.confirmation_source && booking.confirmation_source !== 'legacy_backfill' && booking.confirmation_source !== 'admin_override' && (
                                  <div
                                    className="mb-3 flex items-center gap-1.5 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs"
                                    data-testid={`confirmed-by-you-badge-${booking.id}`}
                                  >
                                    <span className="font-medium">
                                      ✓ Confirmed by you · {new Date(booking.confirmed_at).toLocaleString()}
                                    </span>
                                  </div>
                                )}

                                {/* Mel directive 2026-08-18 — GGF compliance banner. */}
                                {booking.ggf_blocks_checkin && (
                                  <div
                                    className="mb-3 flex flex-wrap items-center gap-2 px-3 py-2.5 rounded border-l-4 border-red-500 bg-red-50 text-red-900 text-sm"
                                    data-testid={`ggf-required-banner-${booking.id}`}
                                  >
                                    <span className="font-semibold">
                                      GGF Assessment Required
                                    </span>
                                    <span className="text-xs text-red-800/90 flex-1 min-w-[220px]">
                                      Guardian Garage Floors certification is required before you can clock in for this shift.
                                    </span>
                                    <Button
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); navigate(booking.ggf_next_step_url || '/talent/certifications/ggf'); }}
                                      className="bg-red-600 hover:bg-red-700 text-white h-7 text-xs"
                                      data-testid={`ggf-take-assessment-btn-${booking.id}`}
                                    >
                                      Take assessment (~10 min)
                                    </Button>
                                  </div>
                                )}
                                {booking.ggf_required && booking.ggf_completed && (
                                  <div
                                    className="mb-3 flex items-center gap-1.5 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs"
                                    data-testid={`ggf-certified-badge-${booking.id}`}
                                  >
                                    <span className="font-medium">✓ GGF certified — ready to clock in.</span>
                                  </div>
                                )}

                                <div className="space-y-2 text-sm text-muted-foreground">
                                  <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    <span>{booking.venue_name} - {booking.city}, {booking.state}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    <span className="font-medium text-foreground">
                                      {formatDate(booking.shift_date)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4" />
                                    <span>{formatTimeRange(booking.start_time, booking.end_time)}</span>
                                    {booking.timezone ? (
                                      <Badge variant="outline" className="text-xs" data-testid={`booking-tz-${booking.id}`}>
                                        {timezoneLabel(booking.timezone)}
                                      </Badge>
                                    ) : status?.gig_timezone_abbrev && (
                                      <Badge variant="outline" className="text-xs">{status.gig_timezone_abbrev}</Badge>
                                    )}
                                  </div>
                                  {(booking.talent_pay_rate || isFlatPay(booking)) && (
                                    <div className="flex items-center gap-2 flex-wrap" data-testid={`booking-pay-rate-${booking.id}`}>
                                      <DollarSign className="w-4 h-4" />
                                      <span><span className="text-gray-500">{isFlatPay(booking) ? 'Flat pay:' : 'Your rate:'}</span> <span className="font-semibold text-gray-900">{formatPay(booking, booking.talent_pay_rate)}</span></span>
                                      {booking.is_urgent_fill && (
                                        <Badge className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px]" data-testid={`urgent-badge-${booking.id}`}>
                                          ⚡ URGENT FILL
                                          {booking.urgent_fill_uplift_amount != null && ` (+$${Number(booking.urgent_fill_uplift_amount).toFixed(2)}/hr)`}
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Day-of confirmation prompt — auto-shows 24h before
                                    shift start (or whenever admin manually requested) */}
                                {(booking.should_show_day_confirm || (booking.day_confirm_request_sent_at && !booking.day_confirmed_at)) && booking.status !== 'checked_in' && booking.status !== 'completed' && (
                                  <div className={`mt-3 rounded-lg p-3 border ${booking.day_confirm_missed_at ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`} data-testid={`day-confirm-banner-${booking.id}`}>
                                    <div className={`text-sm font-semibold mb-1 ${booking.day_confirm_missed_at ? 'text-red-900' : 'text-amber-900'}`}>
                                      {booking.day_confirm_missed_at ? '⚠ Missed day-of confirmation — please confirm now' : 'Day-of confirmation needed'}
                                    </div>
                                    <div className={`text-xs mb-2 ${booking.day_confirm_missed_at ? 'text-red-800' : 'text-amber-800'}`}>
                                      Please confirm you'll be on-site for your shift. Confirming protects your reliability score.
                                    </div>
                                    <Button
                                      size="sm"
                                      onClick={async () => {
                                        try {
                                          await api.post(`/talent/bookings/${booking.id}/day-confirm`, {});
                                          toast.success("Confirmed — see you there!");
                                          fetchBookings();
                                        } catch (e) {
                                          toast.error(e.response?.data?.detail || 'Failed');
                                        }
                                      }}
                                      className={booking.day_confirm_missed_at ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
                                      data-testid={`day-confirm-btn-${booking.id}`}
                                    >
                                      <CheckCircle className="w-4 h-4 mr-1" /> I'll be there
                                    </Button>
                                  </div>
                                )}
                                {booking.day_confirmed_at && booking.status !== 'checked_in' && booking.status !== 'completed' && (
                                  <div className="mt-3 inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid={`day-confirmed-badge-${booking.id}`}>
                                    <CheckCircle className="w-3 h-3" /> Day confirmed
                                  </div>
                                )}

                                {/* Shift Leader roster panel (only if this talent is the SL) */}
                                {booking.is_shift_lead && isToday(booking.shift_date, booking.timezone) && (
                                  <ShiftLeaderRosterPanel bookingId={booking.id} onRefresh={fetchBookings} />
                                )}

                                {/* Clock status display */}
                                {status?.is_clocked_in && !status?.is_clocked_out && (
                                  <div className="mt-3 bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2 text-sm text-cyan-800">
                                    <span className="font-medium">Clocked in at {status.clock_in_time}</span>
                                    {status.current_gig_time && (
                                      <span className="text-cyan-600 ml-2">(Current time: {status.current_gig_time})</span>
                                    )}
                                  </div>
                                )}
                                {status?.is_clocked_out && (
                                  <div className="mt-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800 flex items-center justify-between gap-2 flex-wrap">
                                    <div>
                                      <CheckCircle className="w-4 h-4 inline mr-1" />
                                      Completed - {status.hours_worked} hours worked
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => navigate(`/talent/post-event/${booking.id}`)}
                                      className="border-pink-200 text-pink-700 hover:bg-pink-50 text-xs"
                                      data-testid={`post-event-btn-${booking.id}`}
                                    >
                                      Post-Event Report
                                    </Button>
                                  </div>
                                )}
                              </div>

                              <div className="flex flex-col gap-2">
                                {isToday(booking.shift_date, booking.timezone) && clockState === 'ready' && (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => handleClockIn(booking)}
                                      className="bg-cyan-500 hover:bg-cyan-600"
                                      data-testid="manual-clock-in-btn"
                                    >
                                      <LogIn className="w-4 h-4 mr-2" />
                                      Clock In
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => navigate(`/talent/checkin/${booking.id}`)}
                                      variant="outline"
                                      className="border-cyan-200 text-cyan-700 hover:bg-cyan-50 text-xs"
                                      data-testid="checkin-page-button"
                                      title="Optional: use QR code from on-site lead"
                                    >
                                      QR / Lead Check-In
                                    </Button>
                                  </>
                                )}
                                {isToday(booking.shift_date, booking.timezone) && clockState === 'clocked_in' && (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => handleClockOut(booking)}
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                      data-testid="manual-clock-out-btn"
                                    >
                                      <LogOut className="w-4 h-4 mr-2" />
                                      Clock Out
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => navigate(`/talent/checkin/${booking.id}`)}
                                      variant="outline"
                                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs"
                                      data-testid="checkout-page-button"
                                      title="Optional: use QR code / signature flow"
                                    >
                                      QR / Lead Check-Out
                                    </Button>
                                  </>
                                )}
                                {/*
                                 * P0-2026-08-05 — Release Gig visibility bug.
                                 * Previously we gated on `status === 'confirmed'`
                                 * only, so talent whose booking was still
                                 * `pending`, `day_confirmed`, or `day_confirm_requested`
                                 * never saw the button and had no way to
                                 * release the shift. The backend (Iter-370)
                                 * already accepts every pre-shift status, so
                                 * the UI must match. We also show a small
                                 * helper line when release is unavailable so
                                 * the user knows why (already started / same day).
                                 */}
                                {(() => {
                                  // Backend now returns an authoritative
                                  // `release_state` computed from the real
                                  // shift start time:
                                  //   'allowed'            → >24h out, self-release OK
                                  //   'within_24h_locked'  → policy lock; must contact Evie
                                  //   'started'/'unavailable' → not releasable here
                                  const releaseState = booking.release_state
                                    || (booking.status === 'confirmed' && !isToday(booking.shift_date, booking.timezone)
                                        ? 'allowed' : 'unavailable');

                                  if (releaseState === 'allowed') {
                                    return (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-red-200 text-red-600 hover:bg-red-50"
                                        onClick={() => setReleaseModal(booking)}
                                        data-testid="release-booking-button"
                                      >
                                        <XCircle className="w-4 h-4 mr-2" />
                                        Release Gig
                                      </Button>
                                    );
                                  }

                                  if (releaseState === 'within_24h_locked') {
                                    // The Release Gig option is intentionally
                                    // locked within 24h — show a clear
                                    // "Contact Evie to Cancel" action instead of
                                    // a broken/normal release button.
                                    return (
                                      <div className="flex flex-col items-stretch gap-1" data-testid="release-locked-within-24h">
                                        <Button
                                          size="sm"
                                          className="bg-amber-500 hover:bg-amber-600 text-white"
                                          onClick={() => setContactEvieModal(booking)}
                                          data-testid="contact-evie-to-cancel-button"
                                        >
                                          <MessageCircle className="w-4 h-4 mr-2" />
                                          Contact Evie to Cancel
                                        </Button>
                                        <span className="text-[11px] text-amber-700 text-center">
                                          Within 24 hours — can’t self-release
                                          {booking.urgent_cancellation_requested ? ' · reported' : ''}
                                        </span>
                                      </div>
                                    );
                                  }

                                  let reason = null;
                                  if (releaseState === 'started' || isToday(booking.shift_date, booking.timezone)) {
                                    reason = 'Shift started — contact Evie / Operations';
                                  } else if (['checked_in', 'in_progress'].includes(booking.status)) {
                                    reason = 'Already clocked in — contact Operations to release';
                                  } else if (booking.status === 'completed') {
                                    reason = 'Gig completed — release unavailable';
                                  } else if (booking.status === 'cancelled') {
                                    reason = null;
                                  } else {
                                    reason = `Booking in state "${booking.status}" — contact Operations`;
                                  }
                                  return reason ? (
                                    <span
                                      className="text-xs text-muted-foreground italic"
                                      data-testid="release-blocked-reason"
                                    >
                                      {reason}
                                    </span>
                                  ) : null;
                                })()}
                                {!isToday(booking.shift_date, booking.timezone) && (
                                  <span className="text-xs text-center text-muted-foreground">Check-in on gig day</span>
                                )}
                                {/* Iter-156f — Add to Calendar available
                                    on every active booking row. Hidden
                                    on cancelled / no-show / completed
                                    statuses by the API gate. */}
                                {!['cancelled', 'no_show', 'completed'].includes(booking.status) && (
                                  <AddToCalendarButton bookingId={booking.id} />
                                )}
                              </div>
                            </div>

                            <div className="bg-slate-50 rounded-lg p-3 text-sm">
                              <p className="text-muted-foreground">
                                <strong>Client:</strong> {booking.company_name}
                              </p>
                              <p className="text-muted-foreground mt-1">
                                <strong>Venue:</strong> {booking.venue_address}
                              </p>
                            </div>
                          </CardContent>
                          {/* Reimbursements panel */}
                          <div className="px-6 pb-4 -mt-2">
                            <TalentExpensesPanel bookingId={booking.id} bookingStatus={booking.status} />
                          </div>
                        </Card>
                      );
                    })
                )}
              </div>
            </div>

            {/* Past Bookings */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Past Gigs</h2>
              <div className="grid gap-4" data-testid="past-bookings">
                {bookings.filter((b) => isPast(b.shift_date)).length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      No past gigs
                    </CardContent>
                  </Card>
                ) : (
                  bookings
                    .filter((b) => isPast(b.shift_date))
                    .map((booking) => (
                      <Card key={booking.id} className="opacity-75 hover:opacity-100 cursor-pointer transition" data-testid="booking-card" onClick={(e) => { if (e.target.closest('button,a,[role="button"]')) return; navigate(`/talent/bookings/${booking.id}`); }}>
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <h3 className="text-lg font-semibold">{booking.role_name}</h3>
                                <StatusChip status={booking.status} />
                              </div>
                              <div className="space-y-1 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4" />
                                  <span>{booking.venue_name} - {booking.city}, {booking.state}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4" />
                                  <span>{formatDate(booking.shift_date)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {booking.hours_worked && (
                                <div className="text-sm">
                                  <div className="font-medium">{booking.hours_worked} hours</div>
                                  <div className="text-muted-foreground">Worked</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Clock In/Out Confirmation Modal */}
      {clockModal && (
        <Dialog open={!!clockModal} onOpenChange={() => setClockModal(null)}>
          <DialogContent className="max-w-md" data-testid="clock-modal">
            <DialogHeader>
              <DialogTitle>
                {clockModal.type === 'in' ? 'Clock In' : 'Clock Out'}
              </DialogTitle>
              <DialogDescription>
                {clockModal.booking.venue_name} - {clockModal.booking.role_name}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600 mb-4">
                {clockModal.type === 'in' 
                  ? 'Are you ready to clock in for this gig? Your location will be recorded.'
                  : 'Are you ready to clock out? Your total hours will be calculated.'}
              </p>
              {clockStatus[clockModal.booking.id]?.current_gig_time && (
                <p className="text-sm text-gray-500">
                  Current gig time: <strong>{clockStatus[clockModal.booking.id].current_gig_time}</strong>
                </p>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setClockModal(null)} disabled={processing}>
                Cancel
              </Button>
              <Button
                className="bg-cyan-500 hover:bg-cyan-600"
                onClick={() => clockModal.type === 'in' ? handleClockIn(clockModal.booking) : handleClockOut(clockModal.booking)}
                disabled={processing}
                data-testid="confirm-clock-btn"
              >
                {processing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  clockModal.type === 'in' ? 'Confirm Clock In' : 'Confirm Clock Out'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Release Booking Confirmation Modal */}
      {releaseModal && (
        <Dialog open={!!releaseModal} onOpenChange={() => setReleaseModal(null)}>
          <DialogContent data-testid="release-booking-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Release This Gig?
              </DialogTitle>
              <DialogDescription className="pt-3">
                <div className="space-y-2">
                  <p><strong>Role:</strong> {releaseModal.role_name}</p>
                  <p><strong>Date:</strong> {formatDate(releaseModal.shift_date)}</p>
                  <p><strong>Client:</strong> {releaseModal.company_name}</p>
                </div>
                <p className="mt-4 text-amber-600 font-medium">
                  Warning: This action cannot be undone. The gig will be returned to the job board and another talent may be booked.
                </p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setReleaseModal(null)} disabled={releasing}>
                Keep Booking
              </Button>
              <Button
                variant="destructive"
                onClick={handleReleaseBooking}
                disabled={releasing}
                data-testid="confirm-release-btn"
              >
                {releasing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Releasing...</>
                ) : (
                  'Release Gig'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Within-24h cancellation — self-release is intentionally locked.
          Show a clear policy message (not a technical error) and route the
          talent to Evie, who reports it to staffing + shares the coordinator
          text line so a replacement can be found. */}
      {contactEvieModal && (
        <Dialog open={!!contactEvieModal} onOpenChange={() => setContactEvieModal(null)}>
          <DialogContent data-testid="contact-evie-cancel-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Within 24 Hours — Chat with Evie to Cancel
              </DialogTitle>
              <DialogDescription className="pt-2 text-gray-600">
                This shift begins in less than 24 hours, so it can no longer be
                self-released in the app. iEvntür needs immediate notice so a
                replacement can be found. Please chat with Evie to report the
                cancellation — she’ll share the next step (texting the Staffing
                Coordinator) so we can secure coverage right away.
              </DialogDescription>
            </DialogHeader>
            <div className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p><strong>Role:</strong> {contactEvieModal.role_name}</p>
              <p><strong>Date:</strong> {formatDate(contactEvieModal.shift_date)}</p>
              <p><strong>Starts:</strong> {contactEvieModal.start_time}</p>
              {contactEvieModal.company_name && (
                <p><strong>Client:</strong> {contactEvieModal.company_name}</p>
              )}
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setContactEvieModal(null)}>
                Not now
              </Button>
              <Button
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
                onClick={() => { setContactEvieModal(null); navigate('/talent/support'); }}
                data-testid="chat-with-evie-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Chat with Evie
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {backupAckBookingId && (
        <BackupRoleNoticeModal
          open={true}
          bookingId={backupAckBookingId}
          onAcknowledged={() => { fetchBookings(); }}
          onClose={() => setBackupAckBookingId(null)}
        />
      )}
      {/* 2026-09 — Code of Conduct gig-acceptance gate */}
      <GigCodeOfConductModal
        open={!!cocBookingId}
        busy={cocBusy}
        submitLabel="Accept Gig"
        onClose={() => { if (!cocBusy) setCocBookingId(null); }}
        onAgree={acceptGig}
      />
    </div>
  );
};

export default TalentBookings;